"use client";

import { useLanguage } from "@/context/LanguageContext";
import { useBlogCategoryStore } from "@/store/blogCategoryStore";
import { getBlogCategoryLabelI18n, sortByLabel } from "@/lib/taxonomy-labels";

export const MAX_BLOG_CATEGORIES = 4;

interface BlogCategoryMultiSelectProps {
  selectedDocumentIds: string[];
  onToggle: (documentId: string) => void;
  /** Per-instance prefix so two mounted instances (create + edit) never share a testid. */
  testIdPrefix: string;
  disabled?: boolean;
}

export default function BlogCategoryMultiSelect({
  selectedDocumentIds,
  onToggle,
  testIdPrefix,
  disabled = false,
}: BlogCategoryMultiSelectProps) {
  const { t, language } = useLanguage();
  const categories = useBlogCategoryStore((s) => s.categories);

  const sortedCategories = sortByLabel(
    categories.filter((cat) => !cat.archived),
    (cat) => getBlogCategoryLabelI18n(t, cat.slug, cat.name),
    language,
  );

  const atCap = selectedDocumentIds.length >= MAX_BLOG_CATEGORIES;
  const zeroSelected = selectedDocumentIds.length === 0;
  const legendId = `${testIdPrefix}-legend`;
  const messageId = `${testIdPrefix}-message`;
  const message = zeroSelected
    ? t.modals.blog.atLeastOneCategoryRequired
    : atCap
      ? t.modals.blog.maxCategoriesReached
      : null;

  return (
    <div>
      <p id={legendId} className="block text-sm font-medium text-foreground mb-1.5">
        {t.modals.blog.categories}{" "}
        <span className="text-caption font-normal">({t.modals.blog.selectUpToFour})</span>
      </p>
      <div
        role="group"
        aria-labelledby={legendId}
        aria-describedby={message ? messageId : undefined}
        className="space-y-1 max-h-60 overflow-y-auto border border-border rounded-md p-2"
      >
        {sortedCategories.map((cat) => {
          const checked = selectedDocumentIds.includes(cat.documentId);
          const checkboxDisabled = disabled || (!checked && atCap);
          return (
            <label
              key={cat.documentId}
              className="flex items-center gap-2 cursor-pointer hover:bg-sidebar-hover p-2 rounded text-sm"
            >
              <input
                type="checkbox"
                data-testid={`${testIdPrefix}-${cat.slug}`}
                checked={checked}
                disabled={checkboxDisabled}
                onChange={() => onToggle(cat.documentId)}
                aria-describedby={message ? messageId : undefined}
                className="w-4 h-4 rounded border-border"
              />
              <span className="text-foreground">{getBlogCategoryLabelI18n(t, cat.slug, cat.name)}</span>
            </label>
          );
        })}
      </div>
      <p className="text-xs text-caption mt-1">
        {t.modals.blog.categoriesSelected.replace("{count}", String(selectedDocumentIds.length))}
      </p>
      {message && (
        <p id={messageId} role="alert" className="text-xs text-error mt-1">
          {message}
        </p>
      )}
    </div>
  );
}
